// services/progressService.ts
import { collection, getDocs, doc, setDoc, query, where, updateDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { VideoProgress, VideoStatus } from '../types/progress';

export const progressService = {
  async getUserProgress(userId: string): Promise<VideoProgress[]> {
    const progressRef = collection(db, 'user_progress');
    const q = query(progressRef, where('user_id', '==', userId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as VideoProgress));
  },

  async updateVideoProgress(
    userId: string,
    videoId: string,
    status: VideoStatus,
    position?: number
  ): Promise<void> {
    const progressRef = doc(db, 'user_progress', `${userId}_${videoId}`);
    const progressDoc = await getDoc(progressRef);
    
    const data = {
      user_id: userId,
      video_id: videoId,
      status,
      last_position: position ?? 0,
      updated_at: new Date().toISOString(),
      ...(status === 'completed' && { watched_at: new Date().toISOString() }),
    };

    if (progressDoc.exists()) {
      await updateDoc(progressRef, data);
    } else {
      await setDoc(progressRef, { id: `${userId}_${videoId}`, ...data });
    }
  },

  async markVideoComplete(userId: string, videoId: string): Promise<void> {
    return this.updateVideoProgress(userId, videoId, 'completed');
  },

  async markVideoInProgress(userId: string, videoId: string, position: number): Promise<void> {
    return this.updateVideoProgress(userId, videoId, 'in_progress', position);
  },

  async resetVideoProgress(userId: string, videoId: string): Promise<void> {
    return this.updateVideoProgress(userId, videoId, 'not_started', 0);
  },

  async calculateProgress(userId: string, totalVideos: number): Promise<number> {
    if (totalVideos === 0) return 0;
    
    const progressRef = collection(db, 'user_progress');
    const q = query(progressRef, where('user_id', '==', userId), where('status', '==', 'completed'));
    const snapshot = await getDocs(q);
    
    const completedCount = snapshot.size;
    return Math.round((completedCount / totalVideos) * 100);
  },
};
